import React, { useEffect, useState } from "react";
import { GlobeAltIcon, PhoneIcon } from "@heroicons/react/solid";
import { EyeIcon } from "@heroicons/react/outline";
import ReactGA from "react-ga";

import { Listing } from "./types";
import phoneNumberLink from "./phoneNumberLink";
import searchById from "./providerDirectory/searchById";
import { Link, useParams } from "react-router-dom";
import LoaderSpinner from "../LoaderSpinner/LoaderSpinner";
import useGooglePlaceSearch from "./useGooglePlaceSearch";
import { IconWithText } from "../system/icons/Icon";
import linkCleaner from "./linkCleaner";

import type { Place } from "./types";

function formattedWebsite(website: string) {
  if (!website) return;

  if (website.startsWith("http")) {
    return website;
  }

  return `https://${website}`;
}

function RenderedMap({ place, name }: { place: Place; name: string }) {
  const mapComponentRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!place || !mapComponentRef.current) {
      return;
    }

    const {
      geometry: {
        location: { lat, lng },
      },
    } = place;

    const map = new window.google.maps.Map(mapComponentRef.current, {
      center: { lat, lng },
      zoom: 16,
    });

    new google.maps.Marker({
      position: { lat, lng },
      map,
      title: name,
    });
  }, [place]);

  const mapStyle = {
    width: "100%",
    minHeight: "250px",
    height: "100%",
  };

  return <div style={mapStyle} ref={mapComponentRef} />;
}

function ListingDetails({
  displayName,
  address,
  phoneNumber,
  website,
}: Listing) {
  const { place, isLoading } = useGooglePlaceSearch({ address, displayName });

  React.useEffect(() => {
    ReactGA.event({
      category: "KeepSight - Provider Details",
      action: "Shown",
      label: displayName,
    });
  }, []);

  const renderAddress = () => {
    if (!address) return null;

    const {
      addressLine1,
      addressLine2,
      addressLine3,
      suburb,
      postcode,
      state,
    } = address;

    return (
      <div className="mb-4" data-testid="address">
        <div>
          {addressLine1 && `${addressLine1}, `}
          {addressLine2 && `${addressLine2}, `}
          {addressLine3 && `${addressLine3}`}
        </div>
        <div>{`${suburb} ${postcode} ${state}`}</div>
      </div>
    );
  };

  const renderOpeningHours = () => {
    if (!place) return;
    if (!place.opening_hours) return;

    const {
      opening_hours: { weekday_text, open_now },
    } = place;

    const renderOpenNow = () => {
      return open_now ? (
        <span className="text-green-500 font-bold">Open now</span>
      ) : (
        <span className="text-red-500 font-bold">Currently closed</span>
      );
    };

    return (
      <div>
        <h3>Opening Hours</h3>
        {renderOpenNow()}
        <ul className="list-disc list-inside">
          {weekday_text.map((weekday, idx) => (
            <li key={idx}>{weekday}</li>
          ))}
        </ul>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div data-testid="loading-spinner">
        <LoaderSpinner />
      </div>
    );
  }

  const renderWebsite = () => {
    if (!website) return null;

    const url = linkCleaner(website);

    return (
      <IconWithText icon={<GlobeAltIcon />}>
        <a
          href={url}
          className="link"
          target="_blank"
          onClick={async (e) =>
            ReactGA.event({
              category: "KeepSight - Provider Details",
              action: "Visit website",
              label: displayName,
            })
          }
        >
          {url}
        </a>
      </IconWithText>
    );
  };

  const renderCallToAction = () => {
    let styles =
      "inline-block my-4 bg-steel-blue-600 p-3 text-white hover:text-white hover:no-underline";
    if (website) {
      const url = formattedWebsite(website);
      return (
        <a
          href={url}
          className={styles}
          target="_blank"
          onClick={async (e) =>
            ReactGA.event({
              category: "KeepSight - Provider Details",
              action: "Call to Action Button",
              label: displayName,
            })
          }
        >
          <IconWithText icon={<EyeIcon />}>
            Book an eye test online
          </IconWithText>
        </a>
      );
    }

    if (phoneNumber) {
      return (
        <a
          className={styles}
          href={phoneNumberLink(phoneNumber)}
          onClick={async (e) =>
            ReactGA.event({
              category: "KeepSight - Provider Details",
              action: "Call to Action Button",
              label: displayName,
            })
          }
        >
          <IconWithText icon={<EyeIcon />}>
            Call and book an eye test
          </IconWithText>
        </a>
      );
    }
  };

  const renderPhoneNumber = () => {
    if (!phoneNumber) return null;
    return (
      <IconWithText icon={<PhoneIcon />}>
        <a
          className="link"
          href={phoneNumberLink(phoneNumber)}
          data-testid="phone-number"
          onClick={async (e) =>
            ReactGA.event({
              category: "KeepSight - Provider Details",
              action: "Call",
              label: displayName,
            })
          }
        >
          {phoneNumber}
        </a>
      </IconWithText>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8 px-8">
      <div>
        <Link to={"/"} className="link">
          &laquo; Back to search results
        </Link>
        <h1 className="my-4">{displayName}</h1>

        {renderAddress()}

        {renderPhoneNumber()}

        {renderWebsite()}

        {renderCallToAction()}

        {renderOpeningHours()}
      </div>

      <div id="provider-map-container">
        {place && <RenderedMap place={place} name={displayName} />}
      </div>
    </div>
  );
}

function ListingDetailsContainer({
  listings = [],
  id: propId,
}: {
  listings?: Array<Listing>;
  id?: string;
}) {
  const params = useParams();
  const [listing, setListing] = useState<Listing>();
  const [notFound, setNotFound] = useState<boolean>(false);

  const id = propId || params.id;

  if (!id) {
    throw new Error(
      "Id was not passed to component, pass via params or directly to component as a prop"
    );
  }

  useEffect(() => {
    (async () => {
      const findListing = (id: string) =>
        listings.find((listing) => listing.id === id);

      const localListing = findListing(id);
      if (localListing) {
        setListing(localListing);
        return;
      }

      try {
        const listing = await searchById(id);
        setListing(listing);
      } catch (e) {
        window.Rollbar.error(`Could not find provider result: ${params.id}`);
        setNotFound(true);
      }
    })();
  }, [id]);

  if (!listing || notFound === true) {
    return (
      <div className="my-8">
        <p className="text-xl">
          For some reason, we could not find a listing for: <em>{params.id}</em>
        </p>
        <p>
          <strong>
            This missing listing has been reported and we are working on a fix.
          </strong>
        </p>
        <p>
          <a href="/provider_search">Try a new search</a>
        </p>
      </div>
    );
  }

  return <ListingDetails {...listing} />;
}

export default ListingDetailsContainer;
